import { localApiUrl, locales, mainApiUrl, storeId, currencies } from '@constants'
import baseX from 'base-x'
import { setLocale as setLocaleOriginal } from 'react-i18nify'
import jwtDecode from 'jwt-decode'
import Bugsnag from '@bugsnag/js'
import BugsnagPluginReact from '@bugsnag/plugin-react'
import BugsnagPerformance from '@bugsnag/browser-performance'
import store from './store'
import dayjs from 'dayjs'
import ms from 'ms'
import { clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import { es, pt, ca, fr } from "react-day-picker/locale"
import { defaultCountries, parseCountry } from 'react-international-phone'

const appVersion = import.meta.env.PACKAGE_VERSION
const bugsnagApiKey = import.meta.env.VITE_BUGSNAG_KEY

const bugsnagClient = Bugsnag.start({
  apiKey: bugsnagApiKey,
  appVersion,
  autoTrackSessions: false,
  generateAnonymousId: false,
  plugins: [new BugsnagPluginReact()],
  enabledReleaseStages: ['production'],
  metadata: {
    store_id: storeId,
    location: `${window.document.location}`,
  },
})

Bugsnag.addOnError((...params) => console.log(...params))

BugsnagPerformance.start({
  apiKey: bugsnagApiKey,
  appVersion,
  settleIgnoreUrls: ['https://local.fullfoto.com/api'],
})

const dayPickerLocales = { es, pt, ca, fr }

const base62 = baseX('0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ')

const getReactInternationalPhoneCountries = () => {
  return defaultCountries.map((country) => {
    const { iso2 } = parseCountry(country)

    if (iso2 === 'ar') {
      const newCountryAr = [
        'Argentina',
        'ar',
        '54',
        '(...) ........',
        0,
      ]

      return newCountryAr
    }

    return country
  })
}

const photoIsVertical = ({ width, height, exif }) => {
  return (width && height && height > width) || (exif.Orientation && (exif.Orientation.includes('90') || exif.Orientation.includes('270')))
}

const getPhotoLambdaEndpoint = (photo) => {
  const index = Number(photo.created_at.substr(-2, 1))

  const endpoints = [
    'https://faas-fra1-afec6ce7.doserverless.co/api/v1/web/fn-6173576f-8ef7-4335-9fda-abd3bcb67073/fullfoto/get_thumb00',
    'https://faas-fra1-afec6ce7.doserverless.co/api/v1/web/fn-6173576f-8ef7-4335-9fda-abd3bcb67073/fullfoto/get_thumb01',
    'https://faas-fra1-afec6ce7.doserverless.co/api/v1/web/fn-6173576f-8ef7-4335-9fda-abd3bcb67073/fullfoto/get_thumb02',
    'https://faas-sfo3-7872a1dd.doserverless.co/api/v1/web/fn-7d9befec-0371-483b-b1db-a15cc00ef4df/fullfoto/get_thumb00',
    'https://faas-sfo3-7872a1dd.doserverless.co/api/v1/web/fn-7d9befec-0371-483b-b1db-a15cc00ef4df/fullfoto/get_thumb01',
    'https://faas-sfo3-7872a1dd.doserverless.co/api/v1/web/fn-7d9befec-0371-483b-b1db-a15cc00ef4df/fullfoto/get_thumb02',
    'https://faas-sfo3-7872a1dd.doserverless.co/api/v1/web/fn-7d9befec-0371-483b-b1db-a15cc00ef4df/fullfoto/get_thumb03',
    'https://faas-syd1-c274eac6.doserverless.co/api/v1/web/fn-2817529f-1520-4772-b8bd-6f7456ae98b8/fullfoto/get_thumb00',
    'https://faas-syd1-c274eac6.doserverless.co/api/v1/web/fn-2817529f-1520-4772-b8bd-6f7456ae98b8/fullfoto/get_thumb01',
    'https://faas-syd1-c274eac6.doserverless.co/api/v1/web/fn-2817529f-1520-4772-b8bd-6f7456ae98b8/fullfoto/get_thumb02',
  ]

  const endpointIndex = Math.round(index / (9 / (endpoints.length - 1 )))

  return endpoints[endpointIndex]
}

const buildRedsysForm = ({ action, body }) => {
  const form = document.createElement("form")
  form.setAttribute("action", action)
  form.setAttribute("method", "POST")
  form.setAttribute("style", "display: none")

  for (const key in body) {
    const field = document.createElement("input")
    field.setAttribute("type", "hidden")
    field.setAttribute("id", key)
    field.setAttribute("name", key)
    field.setAttribute("value", body[key])
    form.appendChild(field)
  }

  return form
}

const buildTransbankForm = ({ url, token }) => {
  const form = document.createElement("form")
  form.setAttribute("action", url)
  form.setAttribute("method", "POST")
  form.setAttribute("style", "display: none")

  const field = document.createElement("input")
  field.setAttribute("type", "hidden")
  field.setAttribute("name", 'token_ws')
  field.setAttribute("value", token)
  form.appendChild(field)

  return form
}

const slugify = (str) => String(str).normalize('NFKD').replace(/[\u0300-\u036f]/g, '').trim().replace(/[^a-z0-9A-Z -]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').replace(/^-|-$/, '')

const getFormat = (inputDate) => {
  const now = dayjs()
  const date = dayjs(inputDate)

  if (now.year() !== date.year()) {
    return 'D MMM YYYY | H:mm'
  }

  if (now.format('L') === date.format('L')) {
    return 'H:mm'
  }

  return 'D MMM | H:mm'
}


const formatNotes = (notes, locale) => {
  const matches = notes.match(/\[.{23}Z\]/g)

  if (matches && matches.length) {
    return matches.reduce((acc, i, index, input) => {
      return acc.replace(i, dayjs(i.slice(1, -1)).locale(locale).format('D MMM H:mm'))
    }, notes)
  }

  return notes
}

const draftUpdater = (result) => (draft) => {
  if (!draft) {
    return null
  }

  const index = draft.data.findIndex((i) => i.id === result.id)
  draft.data[index] = result
}

const generateOrderLink = ({ order, action = 'view', source = 'generic' }) => {
  if (!order || !order.id || !order.token) {
    return null
  }

  return `${mainApiUrl}/orders/${base62Encode(JSON.stringify({ order: { id: order.id }, token: order.token, action, source }))}`
}

const rotateArray = (input, times = 1) => {
  if (!input.length || input.length === 1) {
    return input
  }

  const output = [ ...input ]
  const reverse = times < 0

  for (const i of new Array(Math.abs(times)).fill()) {
    if (reverse) {
      output.unshift(output.pop())
      continue
    }

    output.push(output.shift())
  }

  return output
}

const isNumeric = i => !isNaN(parseFloat(i)) && isFinite(i)

const bugsnagNotify = (...params) => {
  console.log(...params)
  return bugsnagClient.notify(...params)
}

const bugsnagNotifyWarning = (e, metadata) => {
  return bugsnagNotify(e, (event) => {
    event.severity = 'warning'

    if (metadata) {
      event.addMetadata('metadata', metadata)
    }
  })
}

const getRandomColor = () => {
  return [Math.floor(Math.random() * 256), Math.floor(Math.random() * 256), Math.floor(Math.random() * 256)]
}

const areColorsEqual = (colorA, colorB) => {
  return colorA[0] === colorB[0] && colorA[1] === colorB[1] && colorA[2] === colorB[2]
}

const bugsnagSetUser = (...params) => {
  return bugsnagClient.setUser(...params)
}

const getOrientationForDeg = (deg) => {
  if (deg === 90) {
    return 6
  }
  if (deg === 180) {
    return 3
  }
  if (deg === 270) {
    return 8
  }
  return 1
}

const formatSeconds = (input) => {
  const seconds = Math.round(input || 0)
  const outputHours = Math.floor(seconds / (60 * 60))

  const minutesDivisor = seconds % (60 * 60)
  const outputMinutes = Math.floor(minutesDivisor / 60)

  const secondsDivisor = minutesDivisor % 60
  const outputSeconds = Math.ceil(secondsDivisor)

  if (outputHours) {
    return `${outputHours}h ${outputMinutes}m ${outputSeconds}s`
  }

  if (outputMinutes) {
    return `${outputMinutes}m ${outputSeconds}s`
  }

  return `${outputSeconds}s`
}

const formatBytes = (bytes, decimals = 2) => {
  if (bytes === 0) {
    return '0 Kb'
  }

  if (!bytes) {
    return null
  }

  if (bytes < 1000000) {
    return `${Math.round((bytes / 1000) * (10 ** decimals)) / (10 ** decimals)} Kb`
  }

  if (bytes < 100000000) {
    return `${Math.round((bytes / 1000 / 1000) * (10 ** decimals)) / (10 ** decimals)} MB`
  }

  if (bytes < 1000000000000) {
    return `${Math.round((bytes / 1000 / 1000 / 1000) * (10 ** decimals)) / (10 ** decimals)} GB`
  }

  return `${Math.round((bytes / 1000 / 1000 / 1000 / 1000) * (10 ** decimals)) / (10 ** decimals)} TB`
}

const getRandomId = ({ uuid = false } = {}) => {
  if (window.crypto && window.crypto.randomUUID) {
    const randomUUID = window.crypto.randomUUID()

    if (uuid) {
      return randomUUID
    }

    return randomUUID.slice(0, 8)
  }

  return Math.random().toString(16).substr(2, 8)
 }

const getTotalReducer = (priceKeyLowerCase) => (acc, i) => {
  if (i.is_gift) {
    return acc
  }

  return (Number(i[priceKeyLowerCase]) || 0) + acc
}

const getTotalsForAlbumItems = (items, tiers, breakdown = {}) => {
  const giftsItems = items.filter(i => i.is_gift)

  if (!breakdown['gift']) {
    breakdown['gift'] = []
  }

  breakdown.gift.push(...giftsItems)

  const paidItems = items.filter(i => !i.is_gift)
  const tierIndex = [ ...tiers ].sort((a, b) => a.quantity - b.quantity).map(i => paidItems.length >= i.quantity).lastIndexOf(true)

  if (tierIndex === -1) {
    if (paidItems.length) {
      if (!breakdown['1']) {
        breakdown['1'] = []
      }

      breakdown['1'].push(...paidItems)
    }

    return { ...currencies.reduce((acc, { slug, priceKeyLowerCase }) => ({ ...acc, [`total_${slug}`]: paidItems.reduce(getTotalReducer(priceKeyLowerCase), 0) }), {}), breakdown }
  }

  const tier = tiers[tierIndex]

  const remaining = paidItems.slice(tier.quantity)

  if (!breakdown[tier.quantity]) {
    breakdown[tier.quantity] = []
  }

  breakdown[tier.quantity].push(...paidItems.slice(0, tier.quantity))

  if (!remaining.length) {
    const reducer = (acc, { totalKeyLowerCase, photoPriceKeyLowerCase, priceKeyLowerCase }) => {
      return { ...acc, [totalKeyLowerCase]: Number(tier[photoPriceKeyLowerCase] || paidItems.reduce(getTotalReducer(priceKeyLowerCase), 0)) }
    }

    return { ...currencies.reduce(reducer, {}), breakdown }
  }

  const totals = getTotalsForAlbumItems(remaining, tiers, breakdown)

  return { ...currencies.reduce((acc, { totalKeyLowerCase, photoPriceKeyLowerCase, priceKeyLowerCase }) => ({ ...acc, [totalKeyLowerCase]: Number(tier[photoPriceKeyLowerCase] || paidItems.slice(0, tier.quantity).reduce(getTotalReducer(priceKeyLowerCase), 0)) + Number(totals[totalKeyLowerCase]) }), {}), breakdown }
}

const getTotalForCurrency = (paymentData, currencySlug) => {
  if (!currencySlug) {
    return null
  }

  const keyEnding = ucFirst(currencySlug.toLowerCase())

  return paymentData[`total${keyEnding}`]
}

const getOriginalTotalForCurrency = (paymentData, currencySlug) => {
  if (!currencySlug) {
    return null
  }

  const keyEnding = ucFirst(currencySlug.toLowerCase())

  return paymentData[`originalTotal${keyEnding}`]
}

const getOrderTotal = (paymentData, addOnsPaymentData, currencySlug) => {
  return getTotalForCurrency(paymentData, currencySlug) + getTotalForCurrency(addOnsPaymentData, currencySlug)
}

const pluck = (input = [], key = '') => {
  if (!key) {
    return input
  }

  return input.map(i => i[key])
}

const getOrderTotalSelector = (currencySlug) => {
  return ({ cart: { paymentData, addOnsPaymentData, loadedCart } }) => {
    if (loadedCart && loadedCart.id && loadedCart[`total_${currencySlug}`]) {
      return loadedCart[`total_${currencySlug}`]
    }
    return getOrderTotal(paymentData, addOnsPaymentData, currencySlug)
  }
}

const getOrderTotalsSelector = () => {
  return ({ cart: { paymentData, addOnsPaymentData, loadedCart } }) => {
    return currencies.reduce((acc, { slug }) => {
      if (loadedCart && loadedCart.id && loadedCart[`total_${slug}`]) {
        return { ...acc, [`total${ucFirst(slug)}`]: loadedCart[`total_${slug}`] }
      }

      return { ...acc, [`total${ucFirst(slug)}`]: getOrderTotal(paymentData, addOnsPaymentData, slug) || 0 }
    }, {})
  }
}

const getTotalsForItems = (items, albums = []) => {
  const currenciesTotals = currencies.reduce((acc, { slug }) => ({ ...acc, [slug]: [] }), {})

  const breakdown = []
  const groupedItems = groupBy(items, 'album.id')

  for (const albumId in groupedItems) {
    const albumItems = groupedItems[albumId]
    const album = (albums || []).find(({ id }) => id === albumId)
    const details = []

    if (!album || !album.photo_price_tiers || !album.photo_price_tiers.length) {
      const totals = getTotalsForAlbumItems(albumItems, [])

      for (const { slug, totalKeyLowerCase } of currencies) {
        currenciesTotals[slug].push(totals[totalKeyLowerCase])
      }

      breakdown.push({ album, data: totals.breakdown })

      continue
    }

    const totals = getTotalsForAlbumItems(albumItems, album.photo_price_tiers)

    for (const { slug, totalKeyLowerCase } of currencies) {
      currenciesTotals[slug].push(totals[totalKeyLowerCase])
    }

    breakdown.push({ album, data: totals.breakdown })
  }

  return { ...currencies.reduce((acc, { slug, totalKey }) => ({ ...acc, [totalKey]: currenciesTotals[slug].reduce((acc1, i) => i + acc1, 0) }), {}), breakdown }
}

const groupBy = (list, key = 'group') => {
  const keys = key.split('.')

  return list.reduce((hash, obj) => {
    const outputKey = keys.reduce((acc, i) => acc[i] || '?', obj)

    return { ...hash, [outputKey]: (hash[outputKey] || []).concat(obj) }
  }, {})
}

const totalInStoreCurrency = (item, currency) => {
  const amount = item[`total_${currency}`]

  return inStoreCurrency(amount, currency)
}

const inStoreCurrency = (amount, slug) => {
  const currency = currencies.find(i => i.slug === slug)
  const value = Math.round(amount * 100) / 100

  if (!currency) {
    return `${Intl.NumberFormat().format(value)}`
  }

  return `${currency.label} ${Intl.NumberFormat().format(value)}`
}

const ucFirst = (input = null) => {
  if (!input) {
    return ''
  }

  const text = String(input)

  return `${text.charAt(0).toUpperCase()}${text.slice(1)}`
}

const isDate = (value) => {
  return value instanceof Date && !isNaN(value.valueOf())
}

const isSameDay = (d1, d2) => {
  if (!d1 || !d2) {
    return false
  }
  return (
    d1.getDate() === d2.getDate() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getFullYear() === d2.getFullYear()
  )
}

const isDayBefore = (d1, d2) => {
  const day1 = new Date(d1.getTime()).setHours(0, 0, 0, 0)
  const day2 = new Date(d2.getTime()).setHours(0, 0, 0, 0)
  return day1 < day2
}

const getPhotographerName = (photographer) => {
  const { name, number } = photographer

  const maxLength = 11
  const lengthNumber = number ? String(number).length + 3 : 0

  if (!lengthNumber) {
    return `${name.slice(0, maxLength)}`
  }

  if (name.length > maxLength - lengthNumber) {
    return `${name.slice(0, maxLength - lengthNumber)}... [${number}]`
  }

  return `${name.slice(0, maxLength - lengthNumber)} [${number}]`
}

const getFacesMapper = ({ width, height, imgWidth, imgHeight }) => ({ rectangle, id }, index) => {
  const aspectRatioPhoto =  width / height
  const aspectRatioImg = imgWidth / imgHeight
  const offsetIsVertical = aspectRatioImg > aspectRatioPhoto

  const widthMultiplier = (offsetIsVertical ? imgWidth : imgHeight * aspectRatioPhoto) / width
  const heightMultiplier =  (offsetIsVertical ? imgWidth / aspectRatioPhoto : imgHeight) / height

  const left = rectangle.tl_corner.x * widthMultiplier
  const top = rectangle.tl_corner.y * heightMultiplier
  const rectangleWidth = rectangle.width * widthMultiplier
  const rectangleHeight = rectangle.height * heightMultiplier

  const topPercent = Math.round((top / (imgHeight)) * 10_000) / 100
  const leftPercent = Math.round((left / (imgWidth)) * 10_000) / 100
  const rectangleHeightPercent = Math.round((rectangleHeight / (imgHeight)) * 10_000) / 100
  const rectangleWidthPercent = Math.round((rectangleWidth / (imgWidth)) * 10_000) / 100

  return { id, left, top, rectangleWidth, rectangleHeight, topPercent, leftPercent, rectangleHeightPercent, rectangleWidthPercent }
}

const truncateText = (input = null, length = 10) => {
  if (!input) {
    return ''
  }

  if (input.length <= length) {
    return input
  }

  if (length < 3) {
    return `${input.slice(0, length)}...`
  }

  return `${input.slice(0, length - 2)}...`
}

const capitalize = (input = null) => {
  if (!input) {
    return ''
  }

  return input.split(' ').map(ucFirst).join(' ')
}

const getBackgroundStyleForPhoto = (photo) => {
  return { backgroundImage: `url(${photo.thumb_base64 || ''})`, backgroundSize: 'cover', backgroundPosition: 'center' }
}

const getUrlForPhoto = (photo, format = '160x160', fallback = '') => {
  if (!photo || !photo.id) {
    if (fallback) {
      return fallback
    }

    if (photo.is_vertical) {
      const [ width, height ] = format.split('x')

      return `/images/no_image_${height}x${width}.jpg`
    }

    return `/images/no_image_${format}.jpg`
  }

  const { main: { isStore } } = store.getState()
  const filename = `${photo.id}.${(photo.filename || '?.jpg').split('.').pop().toLowerCase()}`
  const takenAt = new Date(photo.taken_at)

  if (isStore) {
    const url = `${localApiUrl}/images/${takenAt.getUTCFullYear()}/${takenAt.getUTCMonth()}/${takenAt.getUTCDate()}/${filename}?f=${format}`

    if (!photo.s3_key_base62) {
      return url
    }

    return `${url}&k=${photo.s3_key_base62}`
  }

  if (photo.status === 'ready') {
    return `https://b2.fullfoto.pics/${photo.album.store.id}/${photo.album.id}/${takenAt.getUTCFullYear()}/${takenAt.getUTCMonth()}/${takenAt.getUTCDate()}/${format}/${filename}`
  }

  if (!photo.s3_key_base62) {
    return fallback
  }

  return `${getPhotoLambdaEndpoint(photo)}?k=${photo.s3_key_base62}&f=${format}`
}

const sleep = async (time = '0') => {
  const delay = (typeof time === 'number') ? time : ms(time)

  return await new Promise(resolve => setTimeout(resolve, delay))
}

const isJwtTokenExpired = (token) => {
  if (!token) {
    return true
  }

  try {
    const user = jwtDecode(token)

    if (!user.exp) {
      return true
    }

    return Date.now() > user.exp * 1_000
  } catch (e) {
    return true
  }
}

const sliceId = (model, length = 4) => {
  if (!model || !model.id) {
    return null
  }

  return model.id.slice(0 - length).toUpperCase()
}

const setLocale = (locale) => {
  if (!locales.find(i => i.slug === locale)) {
    return setLocale('en')
  }

  window.localStorage.setItem(`ff:locale`, JSON.stringify(locale))
  setLocaleOriginal(locale)
}

const getLocale = (locale) => {
  const savedState = window.localStorage.getItem(`ff:locale`)

  if (savedState) {
    return JSON.parse(savedState)
  }

  return window.navigator.language ? window.navigator.language.slice(0, 2) : 'es'
}

const base62Encode = (string) => {
  return base62.encode(
    Array(string.length)
      .fill()
      .map((i, index) => string.charCodeAt(index))
  )
}

const base62Decode = (string) => {
  return String.fromCharCode(...base62.decode(string))
}

const getISOString = (i) => {
  if (i instanceof Date) {
    return i.toISOString()
  }

  if (isNumeric(i)) {
    return new Date(i).toISOString()
  }

  return i
}

const formatDate = (date, format = 'dddd D MMMM | H:mm') => {
  if (!date) {
    return ''
  }

  const isoString = getISOString(date)

  return dayjs(isoString).locale(getLocale()).format(format)
}

const cn = (...inputs) => {
  return twMerge(clsx(inputs))
}

export {
  cn,
  dayPickerLocales,
  getTotalReducer,
  totalInStoreCurrency,
  inStoreCurrency,
  groupBy,
  ucFirst,
  truncateText,
  getFacesMapper,
  getPhotographerName,
  isDate,
  isSameDay,
  isDayBefore,
  capitalize,
  getRandomId,
  sliceId,
  getUrlForPhoto,
  isJwtTokenExpired,
  sleep,
  getBackgroundStyleForPhoto,
  base62Encode,
  base62Decode,
  setLocale,
  getLocale,
  formatDate,
  formatBytes,
  formatSeconds,
  getOrientationForDeg,
  bugsnagClient,
  bugsnagNotify,
  bugsnagNotifyWarning,
  getRandomColor,
  areColorsEqual,
  bugsnagSetUser,
  isNumeric,
  rotateArray,
  buildRedsysForm,
  buildTransbankForm,
  generateOrderLink,
  formatNotes,
  getFormat,
  draftUpdater,
  getTotalsForItems,
  getTotalForCurrency,
  getOriginalTotalForCurrency,
  getOrderTotal,
  getOrderTotalSelector,
  getOrderTotalsSelector,
  pluck,
  slugify,
  photoIsVertical,
  getReactInternationalPhoneCountries,
}
